import React, { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import useStore from "../../../context/useStore";
import dataService from "../../../helpers/dataService";
import tools from "../../../helpers/tools";
import {
  ActiveFilterChips,
  DataTable,
  InlineFilters,
  DateCell,
  ListFilters,
  ListPageHeader,
  ListSearchForm,
  Pill,
  SearchToolbar,
  TablePagination,
  formatResultCount,
  useListPage,
} from "../../common/DataList";
import useOperateursAccompagnement from "../../Hooks/useOperateursAccompagnement/useOperateursAccompagnement";
import useOrganizations from "../../Hooks/useOrganizations/useOrganizations";
import usePersistedState from "../../Hooks/usePersistedState/usePersistedState";
import { contratEchu, estCompteOperateur, etatCompte, libelleRole, nomComplet, rolesDisponibles } from "../UserDetail/utilisateurHelpers";
import {
  DELAI_RECHERCHE,
  GROUPES_FILTRES_UTILISATEURS,
  USERS_DEFAULT_FILTERS,
  USERS_ENDPOINT,
  USERS_PERSIST_KEY,
  buildUsersPayload,
  definitionsFiltresUtilisateurs,
  nomStructure,
  nomsOrganisations,
  usersAtoms,
} from "./userListConfig";

// ============================================================
// Liste des utilisateurs sur le kit DataList : en-tête et création, barre de
// recherche (nom, prénom, email, téléphone) avec filtre par rôle, tableau à
// colonnes composées (utilisateur + email, rôle, rattachement, état du
// compte, dernière connexion, fin de contrat), pagination côté serveur.
// Un compte d'opérateur d'accompagnement (CE-1407) montre sa structure à la
// place des organisations ; le panneau filtre par structure.
// ============================================================

export const FILTERS_PANEL_ID = "user-list-filters";
const FILTERS_OPEN_KEY = "espoar.boa.userList.filtersOpen";
const COUNT_LABELS = { none: "Aucun utilisateur", one: "utilisateur", many: "utilisateurs" };

export const getUserColumns = ({ constantes, organizations, operateurs = [], supprimer }) => [
  {
    key: "utilisateur",
    header: "Utilisateur",
    className: "col-recipient",
    render: (u) => (
      <>
        <Link to={`/users/${u.id}`} className="recipient-name">
          {nomComplet(u) || u.email}
        </Link>
        <span className="cell-secondary">{u.email}</span>
      </>
    ),
  },
  {
    key: "role",
    header: "Rôle",
    render: (u) => (
      <>
        <span className="cell-primary">{libelleRole(u.role, constantes)}</span>
        {u.receivesPlanningAlerts && (
          <span className="cell-secondary">
            <i className="fa fa-bell" aria-hidden="true" /> Alertes de planning
          </span>
        )}
      </>
    ),
  },
  {
    key: "rattachement",
    header: "Rattachement",
    className: "col-orgs",
    render: (u) =>
      estCompteOperateur(u) ? (
        <>
          <span className="cell-primary">{nomStructure(u.operator, operateurs) || "Structure inconnue"}</span>
          <span className="cell-secondary">Structure</span>
        </>
      ) : (
        nomsOrganisations(u.organizations, organizations) || <span className="text-muted">—</span>
      ),
  },
  {
    key: "etat",
    header: "État du compte",
    render: (u) => {
      const etat = etatCompte(u);
      return etat ? (
        <Pill tone={etat.ton} icon={etat.icone}>
          {etat.libelle}
        </Pill>
      ) : null;
    },
  },
  {
    key: "lastConnectionAt",
    header: "Dernière connexion",
    className: "col-date",
    render: (u) => <DateCell value={u.lastConnectionAt} empty="Jamais" />,
  },
  {
    key: "contractEndAt",
    header: "Fin de contrat",
    className: "col-date",
    render: (u) =>
      u.contractEndAt ? (
        <>
          <span className={`cell-primary${contratEchu(u) ? " cell-note--warn" : ""}`}>{tools.formatDate(u.contractEndAt)}</span>
          {contratEchu(u) && <span className="cell-secondary cell-note--warn">Contrat échu</span>}
        </>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
  {
    key: "actions",
    header: "Actions",
    headerSrOnly: true,
    className: "col-actions",
    render: (u) => (
      <>
        <Link
          to={`/users/${u.id}`}
          className="btn btn-outline-primary btn-icon"
          aria-label={`Modifier ${nomComplet(u) || u.email}`}
          title="Modifier"
        >
          <i className="fa fa-pen" aria-hidden="true" />
        </Link>
        <button
          type="button"
          className="btn btn-ghost btn-icon ml-1"
          onClick={() => supprimer(u)}
          aria-label={`Supprimer ${nomComplet(u) || u.email}`}
          title="Supprimer"
        >
          <i className="fa fa-trash" aria-hidden="true" />
        </button>
      </>
    ),
  },
];

const UserList = () => {
  const [state] = useStore();
  const items = state.constants.items;
  const constantes = useMemo(() => (Array.isArray(items) ? {} : items), [items]);
  const { organizations, onGetOrganizations } = useOrganizations();
  const { operateurs, onGetOperateurs } = useOperateursAccompagnement();
  const [filtersOpen, setFiltersOpen] = usePersistedState(FILTERS_OPEN_KEY, false);

  const definitions = useMemo(
    () => definitionsFiltresUtilisateurs(rolesDisponibles(constantes), operateurs),
    [constantes, operateurs]
  );
  const list = useListPage({
    searchAtom: usersAtoms.searchAtom,
    resultsAtom: usersAtoms.resultsAtom,
    defaultFilters: USERS_DEFAULT_FILTERS,
    endpoint: USERS_ENDPOINT,
    buildPayload: buildUsersPayload,
    persistKey: USERS_PERSIST_KEY,
    definitions,
    groups: GROUPES_FILTRES_UTILISATEURS,
  });

  useEffect(() => {
    if (!organizations.length) onGetOrganizations();
    if (!operateurs.length) onGetOperateurs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recherche automatique : la liste se relance 800 ms après la dernière
  // modification d'un critère (comportement historique), Entrée n'attend pas.
  const soumettre = useRef(list.submit);
  soumettre.current = list.submit;
  useEffect(() => {
    if (!list.isDirty) return undefined;
    const minuterie = setTimeout(() => soumettre.current(), DELAI_RECHERCHE);
    return () => clearTimeout(minuterie);
  }, [list.filters, list.isDirty]);

  const supprimer = (u) => {
    dataService.remove(`managers/${u.id}`, {}, () => {
      toast.success("L'utilisateur a bien été supprimé");
      list.runSearch(list.search);
    });
  };

  const columns = useMemo(
    () => getUserColumns({ constantes, organizations, operateurs, supprimer }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [constantes, organizations, operateurs, list.search]
  );

  return (
    <div className="container-fluid px-5 my-5 list-page user-list">
      <ListPageHeader
        title="Utilisateurs"
        subtitle="Comptes des agents du back-office : rôles, organisations et sites de rattachement, structure des comptes d'opérateur."
        action={{ to: "/users/create", text: "Créer un utilisateur", dataPriv: "crud_users" }}
      />

      <ListSearchForm onSubmit={list.submit} aria-label="Recherche d'utilisateurs">
        <SearchToolbar
          id="user-search"
          name="terms"
          value={list.filters.terms}
          onChange={list.onFilterChange}
          placeholder="Nom, prénom, email ou téléphone…"
          isLoading={list.isLoading}
          isDirty={false}
          inlineFilters={
            <InlineFilters
              definitions={list.principalFilters}
              filters={list.filters}
              onChange={list.onFilterChange}
              onSet={list.setFilter}
            />
          }
          inlineFiltersLayout="ligne"
          filtersLabel="Plus de filtres"
          filtersPanelId={FILTERS_PANEL_ID}
          filtersOpen={filtersOpen}
          onToggleFilters={list.hasPanelFilters ? () => setFiltersOpen((open) => !open) : null}
          activeFiltersCount={list.panelActiveFiltersCount}
        />
        <ActiveFilterChips chips={list.chips} onRemove={list.removeFilter} onClear={list.resetFilters} />
        {list.hasPanelFilters && (
          <ListFilters
            id={FILTERS_PANEL_ID}
            open={filtersOpen}
            groups={list.groups}
            filters={list.filters}
            onChange={list.onFilterChange}
            onSet={list.setFilter}
            onReset={list.resetFilters}
            canReset={list.canReset}
          />
        )}
      </ListSearchForm>

      <section className="list-card" aria-label="Résultats">
        <div className="results-bar">
          <p className="results-bar__count" role="status" aria-live="polite">
            {formatResultCount(list.count, COUNT_LABELS)}
          </p>
        </div>
        <DataTable
          caption="Liste des utilisateurs du back-office"
          columns={columns}
          rows={list.items}
          rowKey="id"
          isLoading={list.isLoading}
          hasSearched={list.hasSearched}
          error={list.error}
          emptyState={{
            icon: "fa-user-slash",
            title: "Aucun utilisateur ne correspond à ces critères",
            hint: "Modifiez votre recherche ou le filtre de rôle.",
            action: list.canReset ? { label: "Réinitialiser les filtres", onClick: list.resetFilters } : null,
          }}
        />
        <TablePagination
          page={list.page}
          nbPages={list.nbPages}
          count={list.count}
          nbItemsByPage={list.nbItemsByPage}
          onChangePage={list.changePage}
          onChangeNbItemsByPage={list.changeNbItemsByPage}
          itemLabel="utilisateurs"
        />
      </section>
    </div>
  );
};

export default UserList;
